import { useCallback, useEffect, useRef, useState } from "react";
import helpApi from "../api/helpApi";
import { groupBySection } from "../utils/helpUtils";

const SEARCH_DELAY_MS = 250;

/** Contents, the open page and search for the Help view. */
export default function useHelp(initialSlug) {
  const [contents, setContents] = useState([]);
  const [contentsState, setContentsState] = useState("loading"); // loading | ready | error
  const [slug, setSlug] = useState(initialSlug || null);
  const [page, setPage] = useState(null);
  const [pageState, setPageState] = useState("idle"); // idle | loading | ready | missing | error
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let live = true;
    helpApi
      .getContents()
      .then((rows) => {
        if (!live) return;
        setContents(rows);
        setContentsState("ready");
        // Open the first page if nothing was asked for.
        setSlug((s) => s || rows[0]?.slug || null);
      })
      .catch(() => live && setContentsState("error"));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!slug) return undefined;
    let live = true;
    setPageState("loading");
    helpApi
      .getPage(slug)
      .then((p) => {
        if (!live) return;
        setPage(p);
        setPageState("ready");
      })
      .catch((e) => {
        if (!live) return;
        setPage(null);
        setPageState(e?.status === 404 ? "missing" : "error");
      });
    return () => {
      live = false;
    };
  }, [slug]);

  const abortRef = useRef(null);
  useEffect(() => {
    const q = query.trim();
    abortRef.current?.abort();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      helpApi
        .search(q, ctrl.signal)
        .then((rows) => {
          setResults(rows);
          setSearching(false);
        })
        .catch((e) => {
          if (e?.name === "AbortError") return;
          setResults([]);
          setSearching(false);
        });
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const open = useCallback((s) => setSlug(s), []);

  return {
    sections: groupBySection(contents),
    contentsState,
    slug,
    page,
    pageState,
    open,
    query,
    setQuery,
    results,
    searching,
  };
}
